import React, { useState } from 'react';
import { Plus, Trash2, Car, MapPin, Clock, Settings } from 'lucide-react';
import InfoTooltip from '../../../../components/InfoTooltip';

const Step2VehicleTypes = ({ formData, onChange }) => {
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVehicleTypeName, setNewVehicleTypeName] = useState('');

  const vehicleTypes = formData.vehicleTypes || {};

  const defaultVehicleTypes = [
    'SEDAN', 'SUV', 'HATCHBACK', 'BIKE', 'ELECTRIC_SEDAN', 'ELECTRIC_SUV', 'AUTO', 'ELECTRIC_AUTO'
  ];

  // Get all vehicle types (default + custom)
  const allVehicleTypes = [
    ...defaultVehicleTypes,
    ...Object.keys(vehicleTypes).filter(type => !defaultVehicleTypes.includes(type))
  ];

  const addVehicleType = (type) => {
    const newVehicleType = {
      baseFare: 50,
      timeCostPerMin: 2,
      waitTimeCostPerMin: 2,
      rangePricing: [
        { minDistance: 0, maxDistance: 5, type: 'km', value: 15 },
        { minDistance: 5, maxDistance: 15, type: 'km', value: 12 },
        { minDistance: 15, maxDistance: 25, type: 'km', value: 10 },
        { minDistance: 25, maxDistance: null, type: 'km', value: 8 }
      ],
      minFare: 40,
      maxFare: 2500,
      fees: {
        platformFee: { type: 'percentage', value: 10 }
      },
      taxes: {
        gst: { type: 'percentage', value: 5 }
      },
      feeWithTax: {
        convenienceFee: {
          type: 'fixed',
          value: 2,
          tax: {
            cgst: { type: 'percentage', value: 0.5 },
            sgst: { type: 'percentage', value: 0.5 }
          }
        }
      }
    };

    onChange({
      vehicleTypes: {
        ...vehicleTypes,
        [type]: newVehicleType
      }
    });
    setShowAddForm(false);
  };

  const addNewVehicleType = () => {
    if (newVehicleTypeName.trim()) {
      const typeName = newVehicleTypeName.trim().toUpperCase();
      if (!vehicleTypes[typeName]) {
        addVehicleType(typeName);
        setNewVehicleTypeName('');
        setShowAddForm(false);
      } else {
        alert('Vehicle type already exists!');
      }
    }
  };

  const updateVehicleType = (type, updates) => {
    onChange({
      vehicleTypes: {
        ...vehicleTypes,
        [type]: { ...vehicleTypes[type], ...updates }
      }
    });
  };

  const removeVehicleType = (type) => {
    const newVehicleTypes = { ...vehicleTypes };
    delete newVehicleTypes[type];
    onChange({ vehicleTypes: newVehicleTypes });
    if (selectedVehicleType === type) {
      setSelectedVehicleType(null);
    }
  };

  const addRangePricing = (vehicleType) => {
    const currentRanges = vehicleTypes[vehicleType]?.rangePricing || [];
    const newRange = {
      minDistance: currentRanges.length > 0 ? currentRanges[currentRanges.length - 1].maxDistance : 0,
      maxDistance: null,
      type: 'km',
      value: 0
    };
    
    updateVehicleType(vehicleType, {
      rangePricing: [...currentRanges, newRange]
    });
  };

  const updateRangePricing = (vehicleType, index, updates) => {
    const currentRanges = vehicleTypes[vehicleType]?.rangePricing || [];
    const newRanges = [...currentRanges];
    newRanges[index] = { ...newRanges[index], ...updates };
    updateVehicleType(vehicleType, { rangePricing: newRanges });
  };

  const removeRangePricing = (vehicleType, index) => {
    const currentRanges = vehicleTypes[vehicleType]?.rangePricing || [];
    const newRanges = currentRanges.filter((_, i) => i !== index);
    updateVehicleType(vehicleType, { rangePricing: newRanges });
  };

  const addFee = (vehicleType, feeType) => {
    const currentFees = vehicleTypes[vehicleType]?.fees || {};
    updateVehicleType(vehicleType, {
      fees: {
        ...currentFees,
        [feeType]: { type: 'percentage', value: 0 }
      }
    });
  };

  const updateFee = (vehicleType, feeType, updates) => {
    const currentFees = vehicleTypes[vehicleType]?.fees || {};
    updateVehicleType(vehicleType, {
      fees: {
        ...currentFees,
        [feeType]: { ...currentFees[feeType], ...updates }
      }
    });
  };

  const removeFee = (vehicleType, feeType) => {
    const currentFees = { ...vehicleTypes[vehicleType]?.fees || {} };
    delete currentFees[feeType];
    updateVehicleType(vehicleType, { fees: currentFees });
  };

  const addTax = (vehicleType, taxName) => {
    const currentTaxes = vehicleTypes[vehicleType]?.taxes || {};
    updateVehicleType(vehicleType, {
      taxes: {
        ...currentTaxes,
        [taxName]: { type: 'percentage', value: 0 }
      }
    });
  };

  const updateTax = (vehicleType, taxName, updates) => {
    const currentTaxes = vehicleTypes[vehicleType]?.taxes || {};
    updateVehicleType(vehicleType, {
      taxes: {
        ...currentTaxes,
        [taxName]: { ...currentTaxes[taxName], ...updates }
      }
    });
  };

  const removeTax = (vehicleType, taxName) => {
    const currentTaxes = { ...vehicleTypes[vehicleType]?.taxes || {} };
    delete currentTaxes[taxName];
    updateVehicleType(vehicleType, { taxes: currentTaxes });
  };

  const addFeeWithTax = (vehicleType, feeName) => {
    const currentFeeWithTaxes = vehicleTypes[vehicleType]?.feeWithTax || {};
    updateVehicleType(vehicleType, {
      feeWithTax: {
        ...currentFeeWithTaxes,
        [feeName]: {
          type: 'fixed',
          value: 0,
          tax: {}
        }
      }
    });
  };

  const updateFeeWithTax = (vehicleType, feeName, updates) => {
    const currentFeeWithTaxes = vehicleTypes[vehicleType]?.feeWithTax || {};
    updateVehicleType(vehicleType, {
      feeWithTax: {
        ...currentFeeWithTaxes,
        [feeName]: { ...currentFeeWithTaxes[feeName], ...updates }
      }
    });
  };

  const removeFeeWithTax = (vehicleType, feeName) => {
    const currentFeeWithTaxes = { ...vehicleTypes[vehicleType]?.feeWithTax || {} };
    delete currentFeeWithTaxes[feeName];
    updateVehicleType(vehicleType, { feeWithTax: currentFeeWithTaxes });
  };

  const addTaxToFee = (vehicleType, feeName) => {
    const taxName = prompt('Enter tax name:');
    if (taxName) {
      const currentFeeWithTaxes = vehicleTypes[vehicleType]?.feeWithTax || {};
      const currentTaxes = currentFeeWithTaxes[feeName]?.tax || {};
      updateVehicleType(vehicleType, {
        feeWithTax: {
          ...currentFeeWithTaxes,
          [feeName]: {
            ...currentFeeWithTaxes[feeName],
            tax: {
              ...currentTaxes,
              [taxName]: { type: 'percentage', value: 0 }
            }
          }
        }
      });
    }
  };

  const updateTaxInFee = (vehicleType, feeName, taxName, updates) => {
    const currentFeeWithTaxes = vehicleTypes[vehicleType]?.feeWithTax || {};
    const currentTaxes = currentFeeWithTaxes[feeName]?.tax || {};
    updateVehicleType(vehicleType, {
      feeWithTax: {
        ...currentFeeWithTaxes,
        [feeName]: {
          ...currentFeeWithTaxes[feeName],
          tax: {
            ...currentTaxes,
            [taxName]: { ...currentTaxes[taxName], ...updates }
          }
        }
      }
    });
  };

  const removeTaxFromFee = (vehicleType, feeName, taxName) => {
    const currentFeeWithTaxes = vehicleTypes[vehicleType]?.feeWithTax || {};
    const currentTaxes = { ...currentFeeWithTaxes[feeName]?.tax || {} };
    delete currentTaxes[taxName];
    updateVehicleType(vehicleType, {
      feeWithTax: {
        ...currentFeeWithTaxes,
        [feeName]: {
          ...currentFeeWithTaxes[feeName],
          tax: currentTaxes
        }
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Vehicle Types Configuration</h4>
        <p className="text-sm text-gray-600">
          Configure pricing for different vehicle types. Each vehicle type can have different base fares, distance-based pricing, and additional fees.
        </p>
      </div>

      {/* Vehicle Types List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Vehicle Types */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-md font-medium text-gray-900">Available Vehicle Types</h5>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add New Type
            </button>
          </div>
          
          {/* Add New Vehicle Type Form */}
          {showAddForm && (
            <div className="mb-4 p-4 border border-indigo-200 bg-indigo-50 rounded-lg">
              <h6 className="text-sm font-medium text-indigo-900 mb-2">Add New Vehicle Type</h6>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newVehicleTypeName}
                  onChange={(e) => setNewVehicleTypeName(e.target.value)}
                  placeholder="Enter vehicle type name (e.g., MINI_VAN)"
                  className="input input-sm flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addNewVehicleType()}
                />
                <button
                  onClick={addNewVehicleType}
                  className="btn btn-sm btn-primary"
                  disabled={!newVehicleTypeName.trim()}
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewVehicleTypeName('');
                  }}
                  className="btn btn-sm btn-outline"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            {allVehicleTypes.map((type) => (
              <div
                key={type}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedVehicleType === type
                    ? 'border-indigo-500 bg-indigo-50'
                    : vehicleTypes[type]
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedVehicleType(type)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Car className="h-4 w-4 text-gray-600" />
                    <span className="font-medium">{type}</span>
                    {!defaultVehicleTypes.includes(type) && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        Custom
                      </span>
                    )}
                    {vehicleTypes[type] && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        Configured
                      </span>
                    )}
                  </div>
                  {vehicleTypes[type] ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeVehicleType(type);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addVehicleType(type);
                      }}
                      className="text-indigo-600 hover:text-indigo-800"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vehicle Type Configuration */}
        <div>
          {selectedVehicleType && vehicleTypes[selectedVehicleType] ? (
            <div className="space-y-4">
              <h5 className="text-md font-medium text-gray-900">
                Configure {selectedVehicleType}
              </h5>
              
              {/* Basic Pricing */}
              <div className="space-y-3">
                <h6 className="text-sm font-medium text-gray-700">Basic Pricing</h6>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">
                      Base Fare (₹)
                      <InfoTooltip text="Initial fare charged when a trip starts, regardless of distance" />
                    </label>
                    <input
                      type="number"
                      value={vehicleTypes[selectedVehicleType].baseFare}
                      onChange={(e) => updateVehicleType(selectedVehicleType, { baseFare: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Time Cost/Min (₹)
                      <InfoTooltip text="Cost per minute of travel time during the trip" />
                    </label>
                    <input
                      type="number"
                      value={vehicleTypes[selectedVehicleType].timeCostPerMin}
                      onChange={(e) => updateVehicleType(selectedVehicleType, { timeCostPerMin: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Wait Cost/Min (₹)
                      <InfoTooltip text="Cost per minute when the vehicle is waiting for the passenger" />
                    </label>
                    <input
                      type="number"
                      value={vehicleTypes[selectedVehicleType].waitTimeCostPerMin}
                      onChange={(e) => updateVehicleType(selectedVehicleType, { waitTimeCostPerMin: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Min Fare (₹)
                      <InfoTooltip text="Minimum fare that will be charged for any trip, regardless of distance or time" />
                    </label>
                    <input
                      type="number"
                      value={vehicleTypes[selectedVehicleType].minFare}
                      onChange={(e) => updateVehicleType(selectedVehicleType, { minFare: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Max Fare (₹)
                      <InfoTooltip text="Maximum fare cap for any single trip to protect customers" />
                    </label>
                    <input
                      type="number"
                      value={vehicleTypes[selectedVehicleType].maxFare}
                      onChange={(e) => updateVehicleType(selectedVehicleType, { maxFare: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Range Pricing */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h6 className="text-sm font-medium text-gray-700">Distance-based Pricing</h6>
                  <button
                    onClick={() => addRangePricing(selectedVehicleType)}
                    className="btn btn-sm btn-outline"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Range
                  </button>
                </div>
                <div className="space-y-2">
                  {vehicleTypes[selectedVehicleType].rangePricing?.map((range, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 border rounded">
                      <input
                        type="number"
                        value={range.minDistance}
                        onChange={(e) => updateRangePricing(selectedVehicleType, index, { minDistance: parseFloat(e.target.value) || 0 })}
                        className="input input-sm flex-1"
                        placeholder="Min Distance"
                        min="0"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="number"
                        value={range.maxDistance || ''}
                        onChange={(e) => updateRangePricing(selectedVehicleType, index, { maxDistance: e.target.value ? parseFloat(e.target.value) : null })}
                        className="input input-sm flex-1"
                        placeholder="Max Distance"
                        min="0"
                      />
                      <input
                        type="number"
                        value={range.value}
                        onChange={(e) => updateRangePricing(selectedVehicleType, index, { value: parseFloat(e.target.value) || 0 })}
                        className="input input-sm w-20"
                        placeholder="Rate"
                        step="0.01"
                        min="0"
                      />
                      <span className="text-sm text-gray-500">₹/km</span>
                      <button
                        onClick={() => removeRangePricing(selectedVehicleType, index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fees */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h6 className="text-sm font-medium text-gray-700">Fees</h6>
                  <button
                    onClick={() => {
                      const feeName = prompt('Enter fee name:');
                      if (feeName) addFee(selectedVehicleType, feeName);
                    }}
                    className="btn btn-sm btn-outline"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Fee
                  </button>
                </div>
                <div className="space-y-2">
                  {Object.entries(vehicleTypes[selectedVehicleType].fees || {}).map(([feeName, fee]) => (
                    <div key={feeName} className="flex items-center space-x-2 p-2 border rounded">
                      <input
                        type="text"
                        value={feeName}
                        onChange={(e) => {
                          const newName = e.target.value;
                          if (newName !== feeName) {
                            const fees = { ...vehicleTypes[selectedVehicleType].fees };
                            delete fees[feeName];
                            fees[newName] = fee;
                            updateVehicleType(selectedVehicleType, { fees });
                          }
                        }}
                        className="input input-sm w-32"
                        placeholder="Fee name"
                      />
                      <select
                        value={fee.type}
                        onChange={(e) => updateFee(selectedVehicleType, feeName, { type: e.target.value })}
                        className="input input-sm w-20"
                      >
                        <option value="percentage">%</option>
                        <option value="fixed">₹</option>
                        <option value="multiplier">×</option>
                      </select>
                      <input
                        type="number"
                        value={fee.value}
                        onChange={(e) => updateFee(selectedVehicleType, feeName, { value: parseFloat(e.target.value) || 0 })}
                        className="input input-sm flex-1"
                        step="0.01"
                        min="0"
                        placeholder="Value"
                      />
                      <button
                        onClick={() => removeFee(selectedVehicleType, feeName)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Taxes */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h6 className="text-sm font-medium text-gray-700">Taxes</h6>
                  <button
                    onClick={() => {
                      const taxName = prompt('Enter tax name:');
                      if (taxName) addTax(selectedVehicleType, taxName);
                    }}
                    className="btn btn-sm btn-outline"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Tax
                  </button>
                </div>
                <div className="space-y-2">
                  {Object.entries(vehicleTypes[selectedVehicleType].taxes || {}).map(([taxName, tax]) => (
                    <div key={taxName} className="flex items-center space-x-2 p-2 border rounded">
                      <input
                        type="text"
                        value={taxName}
                        onChange={(e) => {
                          const newName = e.target.value;
                          if (newName !== taxName) {
                            const taxes = { ...vehicleTypes[selectedVehicleType].taxes };
                            delete taxes[taxName];
                            taxes[newName] = tax;
                            updateVehicleType(selectedVehicleType, { taxes });
                          }
                        }}
                        className="input input-sm w-32"
                        placeholder="Tax name"
                      />
                      <select
                        value={tax.type}
                        onChange={(e) => updateTax(selectedVehicleType, taxName, { type: e.target.value })}
                        className="input input-sm w-20"
                      >
                        <option value="percentage">%</option>
                        <option value="fixed">₹</option>
                        <option value="multiplier">×</option>
                      </select>
                      <input
                        type="number"
                        value={tax.value}
                        onChange={(e) => updateTax(selectedVehicleType, taxName, { value: parseFloat(e.target.value) || 0 })}
                        className="input input-sm flex-1"
                        step="0.01"
                        min="0"
                        placeholder="Value"
                      />
                      <button
                        onClick={() => removeTax(selectedVehicleType, taxName)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fee with Tax */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h6 className="text-sm font-medium text-gray-700">Fee with Tax</h6>
                  <button
                    onClick={() => {
                      const feeName = prompt('Enter fee name:');
                      if (feeName) addFeeWithTax(selectedVehicleType, feeName);
                    }}
                    className="btn btn-sm btn-outline"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Fee with Tax
                  </button>
                </div>
                <div className="space-y-4">
                  {Object.entries(vehicleTypes[selectedVehicleType].feeWithTax || {}).map(([feeName, feeWithTax]) => (
                    <div key={feeName} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h6 className="text-sm font-medium text-gray-900">{feeName}</h6>
                        <button
                          onClick={() => removeFeeWithTax(selectedVehicleType, feeName)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Fee Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">Fee Name</label>
                          <input
                            type="text"
                            value={feeName}
                            onChange={(e) => {
                              const newName = e.target.value;
                              if (newName !== feeName) {
                                const feeWithTaxes = { ...vehicleTypes[selectedVehicleType].feeWithTax };
                                delete feeWithTaxes[feeName];
                                feeWithTaxes[newName] = feeWithTax;
                                updateVehicleType(selectedVehicleType, { feeWithTax: feeWithTaxes });
                              }
                            }}
                            className="input input-sm w-full"
                            placeholder="Fee name"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Type</label>
                          <select
                            value={feeWithTax.type}
                            onChange={(e) => updateFeeWithTax(selectedVehicleType, feeName, { type: e.target.value })}
                            className="input input-sm w-full"
                          >
                            <option value="percentage">%</option>
                            <option value="fixed">₹</option>
                            <option value="multiplier">×</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Value</label>
                          <input
                            type="number"
                            value={feeWithTax.value}
                            onChange={(e) => updateFeeWithTax(selectedVehicleType, feeName, { value: parseFloat(e.target.value) || 0 })}
                            className="input input-sm w-full"
                            step="0.01"
                            min="0"
                            placeholder="Value"
                          />
                        </div>
                      </div>

                      {/* Taxes for this fee */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs text-gray-500">Taxes on this fee</label>
                          <button
                            onClick={() => addTaxToFee(selectedVehicleType, feeName)}
                            className="btn btn-xs btn-outline"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Tax
                          </button>
                        </div>
                        <div className="space-y-2">
                          {Object.entries(feeWithTax.tax || {}).map(([taxName, tax]) => (
                            <div key={taxName} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                              <input
                                type="text"
                                value={taxName}
                                onChange={(e) => {
                                  const newName = e.target.value;
                                  if (newName !== taxName) {
                                    const taxes = { ...feeWithTax.tax };
                                    delete taxes[taxName];
                                    taxes[newName] = tax;
                                    updateFeeWithTax(selectedVehicleType, feeName, { tax: taxes });
                                  }
                                }}
                                className="input input-sm w-24"
                                placeholder="Tax name"
                              />
                              <select
                                value={tax.type}
                                onChange={(e) => updateTaxInFee(selectedVehicleType, feeName, taxName, { type: e.target.value })}
                                className="input input-sm w-16"
                              >
                                <option value="percentage">%</option>
                                <option value="fixed">₹</option>
                                <option value="multiplier">×</option>
                              </select>
                              <input
                                type="number"
                                value={tax.value}
                                onChange={(e) => updateTaxInFee(selectedVehicleType, feeName, taxName, { value: parseFloat(e.target.value) || 0 })}
                                className="input input-sm flex-1"
                                step="0.01"
                                min="0"
                                placeholder="Value"
                              />
                              <button
                                onClick={() => removeTaxFromFee(selectedVehicleType, feeName, taxName)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Select a vehicle type to configure its pricing
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      {Object.keys(vehicleTypes).length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-blue-800 mb-2">Configuration Summary</h5>
          <div className="text-sm text-blue-700">
            <p>Configured vehicle types: {Object.keys(vehicleTypes).length}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.keys(vehicleTypes).map((type) => (
                <span key={type} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {type}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2VehicleTypes;
